import React, { useState, useEffect } from "react";
import axios from "axios";

export default function NearbyItems() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [radius, setRadius] = useState(5); // Default 5km radius

  useEffect(() => {
    // Get user's location first
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          fetchNearbyItems(position.coords.latitude, position.coords.longitude, radius);
        },
        (err) => {
          setError("Geolocation error: " + err.message);
          setLoading(false);
          // Fallback: fetch all items if location is blocked
          fetchAllItems();
        }
      );
    } else {
      setError("Geolocation is not supported by your browser");
      setLoading(false);
      // Fallback: fetch all items
      fetchAllItems();
    }
  }, [radius]);

  const fetchNearbyItems = async (lat, lng, radius) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/items/nearby?lat=${lat}&lng=${lng}&radius=${radius}`);
      setItems(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch nearby items");
      setLoading(false);
    }
  };

  const fetchAllItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/items");
      setItems(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch items");
      setLoading(false);
    }
  };

  const handleRadiusChange = (e) => {
    setRadius(Number(e.target.value));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">Nearby Lost & Found Items</h1>
      
      {userLocation && (
        <div className="mb-6">
          <label className="block text-gray-700 dark:text-gray-300 mb-2">
            Search within radius (km): {radius}km
          </label>
          <input
            type="range"
            min="1"
            max="50"
            value={radius}
            onChange={handleRadiusChange}
            className="w-full"
          />
        </div>
      )}

      {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

      {loading ? (
        <p>Loading items...</p>
      ) : items.length === 0 ? (
        <p>No items found in your area.</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div key={item._id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white">{item.title}</h2>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.itemType === "lost" 
                      ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" 
                      : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  }`}>
                    {item.itemType}
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">{item.description}</p>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>{item.category}</span>
                  <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                </div>
                {userLocation && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-600 dark:text-gray-300">
                      {calculateDistance(
                        userLocation.lat,
                        userLocation.lng,
                        item.location.coordinates[1],
                        item.location.coordinates[0]
                      ).toFixed(1)} km away
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper function to calculate distance between two coordinates in km
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}